import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "../../components/ui/dialog";
import { api, formatError, formatPrice, formatDate, ORDER_STATUS } from "../../lib/api";
import { AdminTitle, Panel } from "./AdminUi";
import AdminShipping from "./AdminShipping";

const PAY = { pending: "En attente", paid: "Payé", failed: "Échoué", expired: "Expiré", refunded: "Remboursé" };

const OrderDetail = ({ order, onSaved }) => {
  const [status, setStatus] = useState(order.status);
  const [tracking, setTracking] = useState(order.tracking_number || "");
  const [carrier, setCarrier] = useState(order.carrier || "");
  const [trackUrl, setTrackUrl] = useState(order.tracking_url || "");
  const save = async () => {
    try {
      await api.patch(`/admin/orders/${order.id}`, { status, tracking_number: tracking || null, carrier: carrier || null, tracking_url: trackUrl || null });
      toast.success(status !== order.status ? "Statut mis à jour — le client est prévenu par e-mail si nécessaire" : "Enregistré"); onSaved();
    }
    catch (e) { toast.error(formatError(e)); }
  };
  const refund = async () => {
    if (!window.confirm(`Rembourser intégralement ${formatPrice(order.total)} via Stripe ?`)) return;
    try { await api.post(`/admin/orders/${order.id}/refund`); toast.success("Remboursement effectué"); onSaved(); }
    catch (e) { toast.error(formatError(e)); }
  };
  const s = order.shipping;
  return (
    <div className="space-y-5 text-sm" data-testid="order-detail">
      <div className="grid sm:grid-cols-2 gap-4">
        <div><p className="eyebrow mb-2">Client</p><p>{order.customer.first_name} {order.customer.last_name}</p><p className="text-[#F8F6F1]/60">{order.customer.email}</p>{order.customer.phone && <p className="text-[#F8F6F1]/60">{order.customer.phone}</p>}</div>
        <div><p className="eyebrow mb-2">{s.label}</p>
          {s.address && <p className="text-[#F8F6F1]/70">{s.address.line1}{s.address.line2 ? `, ${s.address.line2}` : ""}<br />{s.address.postal_code} {s.address.city}, {s.address.country}</p>}
          {s.relay_point && (
            <p className="text-[#F8F6F1]/70" data-testid="order-relay-point">
              {s.relay_point.carrier} n° {s.relay_point.id}<br />{s.relay_point.name}<br />{s.relay_point.address}, {s.relay_point.postal_code} {s.relay_point.city}
            </p>
          )}
        </div>
      </div>
      <div>
        <p className="eyebrow mb-2">Articles</p>
        {order.items.map((i) => <div key={i.product_id} className="flex justify-between py-1.5 border-b border-[#C9A45C]/10"><span>{i.name} ({i.volume_ml} ml) × {i.quantity}</span><span>{formatPrice(i.price * i.quantity)}</span></div>)}
        <div className="flex justify-between pt-2 text-[#F8F6F1]/60"><span>Réduction {order.promo_code && `(${order.promo_code})`}</span><span>−{formatPrice(order.discount)}</span></div>
        <div className="flex justify-between text-[#F8F6F1]/60"><span>Livraison</span><span>{formatPrice(order.shipping_cost)}</span></div>
        <div className="flex justify-between pt-2 font-display text-xl text-[#C9A45C]"><span>Total</span><span>{formatPrice(order.total)}</span></div>
      </div>
      {order.notes && <p className="text-[#F8F6F1]/60 italic">« {order.notes} »</p>}
      <AdminShipping order={order} onTracking={(shipment) => { setCarrier(shipment.carrier || ""); setTracking(shipment.tracking_number || ""); setTrackUrl(shipment.tracking_url || ""); }} />
      <div className="grid sm:grid-cols-2 gap-3 items-end">
        <label className="block"><span className="text-[10px] tracking-[0.2em] uppercase text-[#F8F6F1]/50">Statut de livraison</span>
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="mt-1.5 w-full bg-[#0B0B0B] border border-[#C9A45C]/25 px-3 py-2.5" data-testid="order-status-select">
            {Object.entries(ORDER_STATUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </label>
        <label className="block"><span className="text-[10px] tracking-[0.2em] uppercase text-[#F8F6F1]/50">N° de suivi</span>
          <input value={tracking} onChange={(e) => setTracking(e.target.value)} className="mt-1.5 w-full bg-[#0B0B0B] border border-[#C9A45C]/25 px-3 py-2.5" data-testid="order-tracking-input" />
        </label>
        <label className="block"><span className="text-[10px] tracking-[0.2em] uppercase text-[#F8F6F1]/50">Transporteur</span>
          <input value={carrier} onChange={(e) => setCarrier(e.target.value)} className="mt-1.5 w-full bg-[#0B0B0B] border border-[#C9A45C]/25 px-3 py-2.5" data-testid="order-carrier-input" />
        </label>
        <label className="block"><span className="text-[10px] tracking-[0.2em] uppercase text-[#F8F6F1]/50">Lien de suivi (https://…)</span>
          <input value={trackUrl} onChange={(e) => setTrackUrl(e.target.value)} className="mt-1.5 w-full bg-[#0B0B0B] border border-[#C9A45C]/25 px-3 py-2.5" data-testid="order-tracking-url-input" />
        </label>
      </div>
      {order.payment_status === "paid" && <button onClick={refund} className="w-full border border-red-400/40 text-red-300 py-3 text-xs tracking-[0.2em] uppercase hover:bg-red-400/10" data-testid="order-refund-button">Rembourser via Stripe</button>}
      <button onClick={save} className="btn-gold w-full !py-3" data-testid="order-status-save">Enregistrer</button>
    </div>
  );
};

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("");
  const [open, setOpen] = useState(null);
  const [archived, setArchived] = useState(false);
  const load = () => api.get("/admin/orders", { params: { ...(filter ? { status: filter } : {}), archived } }).then((r) => setOrders(r.data));
  useEffect(() => { load(); }, [filter, archived]); // eslint-disable-line

  const archiveTests = async () => {
    if (!window.confirm("Archiver toutes les commandes de test ? Cette action est réversible.")) return;
    try {
      const { data } = await api.post("/admin/orders/archive-tests");
      toast.success(data.archived_count + " commande(s) de test archivée(s)");
      load();
    } catch (e) { toast.error(formatError(e)); }
  };
  const toggleArchive = async (order) => {
    if (!window.confirm((order.archived ? "Restaurer " : "Archiver ") + order.order_number + " ?")) return;
    try {
      await api.patch("/admin/orders/" + order.id + "/archive", null, { params: { archived: !order.archived } });
      toast.success(order.archived ? "Commande restaurée" : "Commande archivée");
      setOpen(null); load();
    } catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div data-testid="admin-orders">
      <AdminTitle title="Commandes">Suivez les commandes et mettez à jour les statuts de livraison.</AdminTitle>
      <div className="flex flex-wrap gap-3 mb-5">
        <button type="button" onClick={() => setArchived(false)} className="border border-[#C9A45C]/40 px-4 py-2 text-xs">Actives</button>
        <button type="button" onClick={() => setArchived(true)} className="border border-[#C9A45C]/40 px-4 py-2 text-xs">Archives</button>
        {!archived && <button type="button" onClick={archiveTests} className="border border-[#C9A45C]/40 text-[#C9A45C] px-4 py-2 text-xs" data-testid="archive-test-orders">Archiver toutes les commandes TEST</button>}
      </div>
      <div className="flex gap-2 overflow-x-auto no-scrollbar mb-4">
        {[["", "Toutes"], ...Object.entries(ORDER_STATUS)].map(([k, v]) => (
          <button key={k} onClick={() => setFilter(k)} className={`px-3 py-1.5 text-xs whitespace-nowrap border ${filter === k ? "bg-[#C9A45C] text-[#0B0B0B] border-[#C9A45C]" : "border-[#C9A45C]/25 text-[#F8F6F1]/70"}`} data-testid={`orders-filter-${k || "all"}`}>{v}</button>
        ))}
      </div>
      <Panel className="overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead><tr className="text-left text-[10px] tracking-[0.2em] uppercase text-[#F8F6F1]/45 border-b border-[#C9A45C]/15">
            <th className="p-3">N°</th><th className="p-3">Date</th><th className="p-3">Client</th><th className="p-3">Livraison</th><th className="p-3">Total</th><th className="p-3">Paiement</th><th className="p-3">Statut</th>
          </tr></thead>
          <tbody>
            {orders.map((o) => (
              <tr key={o.id} onClick={() => setOpen(o)} className="border-b border-[#C9A45C]/10 hover:bg-[#C9A45C]/5 cursor-pointer" data-testid={`admin-order-row-${o.order_number}`}>
                <td className="p-3">{o.order_number}{o.is_test && <span className="ml-2 text-[10px] text-[#C9A45C]">TEST</span>}</td>
                <td className="p-3 text-[#F8F6F1]/60">{formatDate(o.created_at)}</td>
                <td className="p-3">{o.customer.first_name} {o.customer.last_name}</td>
                <td className="p-3 text-[#F8F6F1]/60">{o.shipping.label}</td>
                <td className="p-3 text-[#C9A45C]">{formatPrice(o.total)}</td>
                <td className={`p-3 ${o.payment_status === "paid" ? "text-emerald-400" : "text-[#F8F6F1]/50"}`}>{PAY[o.payment_status] || o.payment_status}</td>
                <td className="p-3">{ORDER_STATUS[o.status]}</td>
              </tr>
            ))}
            {!orders.length && <tr><td colSpan={7} className="p-10 text-center text-[#F8F6F1]/45">Aucune commande.</td></tr>}
          </tbody>
        </table>
      </Panel>
      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="bg-[#101010] border-[#C9A45C]/30 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogTitle className="font-display text-2xl font-normal">Commande {open?.order_number}</DialogTitle>
          {open && <><OrderDetail order={open} onSaved={() => { setOpen(null); load(); }} /><button type="button" onClick={() => toggleArchive(open)} className="mt-4 w-full border border-[#C9A45C]/40 py-3 text-xs">{open.archived ? "Restaurer la commande" : "Archiver la commande"}</button></>}
        </DialogContent>
      </Dialog>
    </div>
  );
}

